import React, { useEffect, useState } from 'react';

function FoodStockView({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [error, setError] = useState('');

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = () => ({ Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId });

  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  const load = async () => {
    if (!chainId || !propertyId) return;
    try {
      const res = await fetch('/api/v1/food/stock/realtime', { headers: headers() });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Failed to load stock view');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading stock view');
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    load();
  }, [token, chainId, propertyId]);

  const inputStyle = { padding: '0.5rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const alertText = (r) => [r.low_stock_alert ? 'Low Stock' : null, r.expiry_alert ? 'Expiry' : null, r.overstock_alert ? 'Overstock' : null].filter(Boolean).join(', ') || '-';
  const storeOptions = Array.from(
    new Map(
      rows
        .filter((r) => r && r.store_id)
        .map((r) => [String(r.store_id), { id: String(r.store_id), name: r.store_name || String(r.store_id) }])
    ).values()
  ).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const filteredRows = selectedStoreId
    ? rows.filter((r) => String(r.store_id || '') === String(selectedStoreId))
    : rows;

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Management - Real-Time Stock View</h2>
      {error && <div className="error-banner">{error}</div>}
      <div className="filter-bar">
        <div style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <label htmlFor="food-stock-store-filter" style={{ fontWeight: 600, color: '#2d3748' }}>Store</label>
          <select
            id="food-stock-store-filter"
            value={selectedStoreId}
            onChange={(e) => setSelectedStoreId(e.target.value)}
            style={inputStyle}
          >
            <option value="">All Stores</option>
            {storeOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        {filteredRows.length === 0 ? <p>No stock available</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2f855a', color: '#fff' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Store</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Item</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Current</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Available</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Reserved</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Stock Value</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Nearest Expiry</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Alerts</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, idx) => (
                <tr key={`${r.store_id}-${r.item_id}-${idx}`} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ padding: '0.5rem' }}>{r.store_name}</td>
                  <td style={{ padding: '0.5rem' }}>{`${r.item_code || ''} ${r.item_name || ''}`}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(r.on_hand_qty || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(r.available_qty || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(r.reserved_qty || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(r.stock_value || 0).toFixed(2)}</td>
                  <td style={{ padding: '0.5rem' }}>{r.nearest_expiry ? String(r.nearest_expiry).slice(0, 10) : '-'}</td>
                  <td style={{ padding: '0.5rem', color: (r.low_stock_alert || r.expiry_alert || r.overstock_alert) ? '#c53030' : '#4a5568' }}>{alertText(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodStockView;
